/**
 * NoodcontactModal — verplichte self-service pop-up.
 *
 * Bij inloggen: check /api/me/noodcontact-status.
 * Als is_verplicht_actie=true → toon deze blokkade-modal totdat medewerker
 * noodcontact-naam + telefoon (+ relatie) invult en opslaat.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Shield, PhoneCall } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function NoodcontactModal() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    noodcontact_naam: "",
    noodcontact_telefoon: "",
    noodcontact_relatie: "",
  });

  useEffect(() => {
    // Alleen checken als user is ingelogd
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/noodcontact-status");
        if (cancelled) return;
        setStatus(data);
        if (data?.is_verplicht_actie) {
          // Prefill met bestaande waarden (in geval van gedeeltelijke invulling)
          setForm({
            noodcontact_naam: data.huidige_waarden?.noodcontact_naam || "",
            noodcontact_telefoon: data.huidige_waarden?.noodcontact_telefoon || "",
            noodcontact_relatie: data.huidige_waarden?.noodcontact_relatie || "",
          });
          setOpen(true);
        }
      } catch (e) {
        // Stil falen — als endpoint niet bereikbaar is, willen we login-flow niet blokkeren
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const save = async () => {
    const naam = (form.noodcontact_naam || "").trim();
    const tel = (form.noodcontact_telefoon || "").trim();
    if (!naam) { toast.error("Naam van noodcontact is verplicht."); return; }
    if (!tel) { toast.error("Telefoonnummer is verplicht."); return; }
    if (tel.length < 6) { toast.error("Vul een geldig telefoonnummer in."); return; }
    setBusy(true);
    try {
      await api.put("/me/noodcontact", {
        noodcontact_naam: naam,
        noodcontact_telefoon: tel,
        noodcontact_relatie: (form.noodcontact_relatie || "").trim() || null,
      });
      toast.success("Bedankt! Uw noodcontact is opgeslagen.");
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    setBusy(true);
    try {
      await api.post("/me/noodcontact-skip");
      toast.info("Overgeslagen — bij uw volgende login is invullen verplicht.");
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Overslaan mislukt");
    } finally {
      setBusy(false);
    }
  };

  if (!status?.is_verplicht_actie) return null;

  return (
    <Dialog open={open} onOpenChange={() => { /* Kan niet gesloten worden zonder invullen */ }}>
      <DialogContent
        className="max-w-lg [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="noodcontact-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-[hsl(var(--brand-red))]" />
            Noodcontact toevoegen (verplicht)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Alert>
            <AlertDescription className="text-sm">
              <b>Beste {status.employee_naam || user?.name},</b>
              <div className="mt-1">
                Voor uw veiligheid vraagt BPT om een noodcontactpersoon vast te leggen — iemand die wij mogen bellen bij een ongeval of noodgeval tijdens uw werk. Deze gegevens zijn strikt vertrouwelijk en alleen zichtbaar voor HR en de operationele leiding.
              </div>
            </AlertDescription>
          </Alert>

          <div>
            <Label className="text-sm">Volledige naam noodcontact <span className="text-rose-500">*</span></Label>
            <Input
              value={form.noodcontact_naam}
              onChange={(e) => setForm({ ...form, noodcontact_naam: e.target.value })}
              placeholder="Bijv. Ayşe Yılmaz"
              data-testid="nc-naam"
            />
          </div>
          <div>
            <Label className="text-sm flex items-center gap-1"><PhoneCall className="h-3.5 w-3.5" /> Telefoonnummer <span className="text-rose-500">*</span></Label>
            <Input
              value={form.noodcontact_telefoon}
              onChange={(e) => setForm({ ...form, noodcontact_telefoon: e.target.value })}
              placeholder="+31 6 12345678"
              type="tel"
              data-testid="nc-tel"
            />
          </div>
          <div>
            <Label className="text-sm">Relatie tot u (optioneel)</Label>
            <Input
              value={form.noodcontact_relatie}
              onChange={(e) => setForm({ ...form, noodcontact_relatie: e.target.value })}
              placeholder="Bijv. Partner, Ouder, Broer/zus"
              data-testid="nc-relatie"
            />
          </div>

          <Button
            onClick={save}
            disabled={busy}
            className="w-full bg-[hsl(var(--brand-red))] hover:bg-[hsl(var(--brand-red))]/90 text-white"
            data-testid="nc-save"
          >
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opslaan…</> : "Opslaan en doorgaan"}
          </Button>
          {status?.kan_overslaan && (
            <Button
              variant="outline"
              onClick={skip}
              disabled={busy}
              className="w-full"
              data-testid="nc-skip"
            >
              Nu even overslaan (kan maar 1x)
            </Button>
          )}
          <p className="text-[11px] text-slate-400 text-center">
            U kunt deze gegevens later wijzigen via <b>Mijn gegevens</b> of via HR.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
